import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

// 30 short, age-appropriate motivational lines tailored for Indian school students.
// Picked deterministically by day-of-year so the message stays consistent for everyone
// on the same date but rotates daily.
const QUOTES = [
  { text: "Small efforts every day add up to big results. You're doing great!", emoji: "🌱" },
  { text: "Every question you attempt teaches your brain something new.", emoji: "🧠" },
  { text: "Mistakes are proof that you're trying. Keep going!", emoji: "💪" },
  { text: "You don't have to be perfect — just one step better than yesterday.", emoji: "✨" },
  { text: "Focus on progress, not perfection. You're already winning.", emoji: "🏆" },
  { text: "Today's effort becomes tomorrow's confidence.", emoji: "🚀" },
  { text: "Learn something. Practice it. Master it. That's how heroes are built.", emoji: "🦸" },
  { text: "The expert in anything was once a beginner.", emoji: "🌟" },
  { text: "Your brain grows stronger every time you struggle through a problem.", emoji: "⚡" },
  { text: "Believe you can — and you're already halfway there.", emoji: "🌈" },
  { text: "Every great achiever started exactly where you are right now.", emoji: "🌅" },
  { text: "Hard work beats talent when talent doesn't work hard. — A.P.J. Abdul Kalam", emoji: "🎯" },
  { text: "Dream, dream, dream. Dreams transform into thoughts and thoughts result in action. — Kalam", emoji: "🌠" },
  { text: "The only limit to your impact is your imagination and commitment. — Tony Robbins", emoji: "🔥" },
  { text: "Education is the most powerful weapon you can use to change the world. — Mandela", emoji: "📚" },
  { text: "Don't watch the clock; do what it does — keep going.", emoji: "⏰" },
  { text: "Push yourself, because no one else is going to do it for you.", emoji: "💯" },
  { text: "Success is the sum of small efforts repeated day in and day out.", emoji: "📈" },
  { text: "Be so good they can't ignore you.", emoji: "👑" },
  { text: "A little progress each day adds up to big results.", emoji: "🌻" },
  { text: "The best way to predict the future is to create it.", emoji: "🛠️" },
  { text: "Hard things become easier when you've done them once.", emoji: "🧗" },
  { text: "Curiosity is the best teacher. Stay curious!", emoji: "🔍" },
  { text: "You are capable of more than you know.", emoji: "💫" },
  { text: "Your only competition is who you were yesterday.", emoji: "🪞" },
  { text: "Doubt kills more dreams than failure ever will.", emoji: "🛡️" },
  { text: "Genius is 1% inspiration and 99% perspiration. — Thomas Edison", emoji: "⚙️" },
  { text: "If you can dream it, you can do it. — Walt Disney", emoji: "🎨" },
  { text: "The journey of a thousand miles begins with a single step.", emoji: "👣" },
  { text: "You've got this. One question at a time, one chapter at a time.", emoji: "🤝" },
];

const STORAGE_KEY = 'mcai_daily_quote_lastDate';

const getQuoteOfDay = () => {
  const start = new Date(new Date().getFullYear(), 0, 0);
  const diff = new Date() - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  return QUOTES[dayOfYear % QUOTES.length];
};

const DailyMotivation = () => {
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const last = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (last !== today) {
      setQuote(getQuoteOfDay());
      setOpen(true);
      try { localStorage.setItem(STORAGE_KEY, today); } catch (e) { /* private mode */ }
    }
  }, []);

  if (!open || !quote) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 shadow-md mb-4 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white"
      data-testid="daily-motivation-card"
    >
      <button
        onClick={() => setOpen(false)}
        className="absolute top-3 right-3 text-white/80 hover:text-white"
        aria-label="Dismiss"
        data-testid="dismiss-motivation"
      >
        <X size={18} />
      </button>

      {/* Subtle decorative sparkle */}
      <Sparkles className="absolute -bottom-4 -right-4 text-white/20" size={120} />

      <div className="relative flex items-start gap-4">
        <div className="text-4xl shrink-0" aria-hidden>{quote.emoji}</div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider opacity-80 font-semibold">Today's Motivation</p>
          <p className="text-base md:text-lg font-bold mt-1 leading-snug">{quote.text}</p>
        </div>
      </div>
    </div>
  );
};

export default DailyMotivation;
